import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Download, Globe, Lock, Link as LinkIcon, Play, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function fmtSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0, n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
function fmtDuration(ms) {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (v) => String(v).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}
const VisIcon = { public: Globe, unlisted: LinkIcon, private: Lock };

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PastStreams({ channelUsername }) {
  const { user } = useAuth();
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null); // recording being previewed inline
  const isOwner = !!(user && user.username === channelUsername);

  const load = useCallback(async () => {
    if (!channelUsername) return;
    setLoading(true);
    try {
      const r = await api.get(`/streams/recordings/by-channel/${channelUsername}`);
      setRecs(r.data.recordings || []);
    } catch (e) {
      toast.error("Couldn't load past streams", { description: e?.response?.data?.detail || "Please try again" });
    } finally {
      setLoading(false);
    }
  }, [channelUsername]);

  useEffect(() => { load(); }, [load]);

  const del = async (rec) => {
    if (!window.confirm(`Delete "${rec.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/streams/recordings/${rec.id}`);
      setRecs(rs => rs.filter(r => r.id !== rec.id));
      if (playing?.id === rec.id) setPlaying(null);
      toast.success("Recording deleted");
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || "Please try again" });
    }
  };

  const setVis = async (rec, visibility) => {
    try {
      const r = await api.patch(`/streams/recordings/${rec.id}`, { visibility });
      setRecs(rs => rs.map(x => (x.id === rec.id ? r.data : x)));
      toast.success(`Visibility set to ${visibility}`);
    } catch (e) {
      toast.error("Couldn't update visibility", { description: e?.response?.data?.detail || "Please try again" });
    }
  };

  const downloadUrl = (rec) => {
    const token = localStorage.getItem("sc_token");
    return `${BACKEND}/api/streams/recordings/${rec.id}/download${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  };
  const inlineUrl = (rec) => {
    const token = localStorage.getItem("sc_token");
    return `${BACKEND}/api/streams/recordings/${rec.id}/download?inline=1${token ? `&auth=${encodeURIComponent(token)}` : ""}`;
  };

  return (
    <div data-testid="past-streams">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="sc-label text-[color:var(--sc-cyan)]">Past streams</div>
          <div className="text-[10px] sc-mono uppercase tracking-widest text-zinc-500">
            {isOwner ? `${recs.length}/40 recordings saved` : `${recs.length} public recording${recs.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <button
          data-testid="past-streams-refresh"
          onClick={load}
          className="p-2 rounded-sm border border-white/10 text-zinc-400 hover:border-white/25 hover:text-white"
          title="Refresh list"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {playing && (
        <div className="mb-4 border border-[color:var(--sc-cyan)]/50 rounded-md overflow-hidden" data-testid="past-streams-player">
          <video
            src={inlineUrl(playing)}
            controls
            autoPlay
            className="w-full aspect-video bg-black"
          />
          <div className="px-4 py-3 bg-[#0c0c0e] border-t border-white/10 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{playing.title}</div>
              <div className="text-[10px] sc-mono uppercase tracking-widest text-zinc-500">
                {fmtDuration(playing.duration_ms)} · {fmtSize(playing.size_bytes)} · {playing.view_count || 0} views
              </div>
            </div>
            <button
              onClick={() => setPlaying(null)}
              className="sc-btn-ghost text-[10px]"
            >Close</button>
          </div>
        </div>
      )}

      {loading && recs.length === 0 ? (
        <div className="text-xs text-zinc-500 py-6 text-center">Loading…</div>
      ) : recs.length === 0 ? (
        <div className="text-xs text-zinc-500 py-6 text-center border border-white/5 rounded-sm">
          {isOwner ? "No saved streams yet. End your next live stream and hit Save to build your VOD library." : "This streamer hasn't shared any past streams yet."}
        </div>
      ) : (
        <div className="grid gap-2">
          {recs.map((r) => {
            const VIcon = VisIcon[r.visibility] || Globe;
            return (
              <div
                key={r.id}
                className="p-3 border border-white/5 rounded-sm hover:border-white/15 flex items-center gap-3"
                data-testid={`past-stream-${r.id}`}
              >
                <button
                  onClick={() => setPlaying(r)}
                  className="w-16 h-10 shrink-0 flex items-center justify-center bg-black border border-white/10 rounded-sm text-[color:var(--sc-cyan)] hover:bg-white/5"
                  title="Play"
                  data-testid={`past-stream-play-${r.id}`}
                >
                  <Play className="w-4 h-4" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{r.title}</div>
                  <div className="text-[10px] sc-mono uppercase tracking-widest text-zinc-500 flex items-center gap-2 mt-0.5">
                    <VIcon className="w-3 h-3" />
                    <span>{r.visibility}</span>
                    <span>·</span>
                    <span>{fmtDuration(r.duration_ms)}</span>
                    <span>·</span>
                    <span>{fmtSize(r.size_bytes)}</span>
                    <span>·</span>
                    <span>{new Date(r.created_at).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
                    {!isOwner && (
                      <>
                        <span>·</span>
                        <span>{r.view_count || 0} views</span>
                      </>
                    )}
                  </div>
                </div>
                {isOwner && (
                  <>
                    <select
                      data-testid={`past-stream-visibility-${r.id}`}
                      value={r.visibility}
                      onChange={(e) => setVis(r, e.target.value)}
                      className="sc-input text-[10px] py-1 px-2 w-24"
                      title="Change visibility"
                    >
                      <option value="public">Public</option>
                      <option value="unlisted">Unlisted</option>
                      <option value="private">Private</option>
                    </select>
                  </>
                )}
                <a
                  href={downloadUrl(r)}
                  download
                  data-testid={`past-stream-download-${r.id}`}
                  className="p-2 rounded-sm border border-white/10 text-zinc-400 hover:border-[color:var(--sc-cyan)] hover:text-[color:var(--sc-cyan)]"
                  title="Download"
                >
                  <Download className="w-3.5 h-3.5" />
                </a>
                {isOwner && (
                  <button
                    data-testid={`past-stream-delete-${r.id}`}
                    onClick={() => del(r)}
                    className="p-2 rounded-sm border border-white/10 text-zinc-400 hover:border-[color:var(--sc-pink)] hover:text-[color:var(--sc-pink)]"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
